import { ButtonType, CheckBoxWithLabelWidget } from '@tableau/widgets';
import { IQService } from 'angular';
import { IPromise } from 'q';
import React = require('react');
import _ = require('underscore');
import { Messages } from '../../codegen/Localize';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { DialogInfoAlert } from '../../common/react/components/dialogAlerts/DialogAlerts';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { ISiteRoleFilter } from '../../ts/filters/SiteRoleFilter';
import { ResultHelper } from '../../ts/helpers/ResultHelper';
import { L10n } from '../../ts/L10n/L10n';
import { IGroupBasicInfo, IGroupInfo } from '../../ts/models/IGroupInfo';
import { Set } from '../../ts/models/Set';
import { Groups } from '../../userManagement/groups/Groups';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../../userManagement/services/resources/SiteRoleOptions';
import { GrantLicenseModes } from '../../ts/ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { ConfirmActionDialog } from '../../ts/services/ConfirmActionDialog';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { SiteRolePopover } from '../../userManagement/components/SiteRolePopover';
import { ServiceLocator } from '../ServiceLocator';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { toQPromise } from '../../commonInfra/utils/QUtils';

export interface UpdateGrantLicenseModeDialogState {
  readonly siteRole: string;
  readonly grantLicenseModeCheckboxChecked: boolean;
  readonly busy: boolean;
}

export interface UpdateGrantLicenseModeDialogProps {
  readonly close: (result: any) => void;
  readonly angularContext: AngularContext;
  readonly dismiss: () => void;
  readonly groupsToSet: Set<IGroupInfo>;
  readonly isActiveDirectory?: boolean;
}

export class UpdateGrantLicenseModeDialog extends React.Component<
UpdateGrantLicenseModeDialogProps,
UpdateGrantLicenseModeDialogState
> {
  private $q = ServiceLocator.get<IQService>('$q');
  private translate = ServiceLocator.get<ng.translate.ITranslateService>('$translate');
  private siteRoleOptions = SiteRoleOptionsResource.instance.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);
  private confirmActionDialog: ConfirmActionDialog = ServiceLocator.get<ConfirmActionDialog>('ConfirmActionDialog');
  private CheckBoxContainerStyle: React.CSSProperties = {
    marginLeft: '0px', marginTop: '8px'
  };
  private toaster: ToasterService = ToasterService.instance;
  private $filter: ng.IFilterService = ServiceLocator.get<ng.IFilterService>('$filter');

  public constructor(props: UpdateGrantLicenseModeDialogProps) {
    super(props);
    let siteRole: string = DefaultSiteRole;
    let checkboxChecked: boolean = false;
    if (props.groupsToSet.size() === 0 && this.props.isActiveDirectory) {
      this.confirmActionDialog.open({
        scope: this.props.angularContext.scope,
        title: Messages.minimumSiteRole(),
        bodyText: Messages.minimumSiteRoleNoADGroups()
      });
    }
    else if (props.groupsToSet.size() === 1) {
      let group: IGroupInfo = props.groupsToSet.getByIndex(0);
      siteRole = group.minimumSiteRole && group.minimumSiteRole !== SiteRoles.Unlicensed ? group.minimumSiteRole : siteRole;
      checkboxChecked = group.grantLicenseMode === GrantLicenseModes.ON_LOGIN;
    }
    this.state = {
      siteRole: siteRole,
      grantLicenseModeCheckboxChecked: checkboxChecked,
      busy: false
    };
  }

  private readonly getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(siteRole);
  }

  private readonly onOptionSelectedCallback = (siteRoleOption: SiteRoleOption): void => {
    this.onSiteRoleSelected(siteRoleOption);
  }

  public render() {
    return (
      <div>
        <div className="tb-dialog-title">{Messages.licenseUponLoginDialogTitle()}</div>
        <div className="tb-dialog-body-input-description">{Messages.confirmSetGroupsMinimumSiteRoleDesc({
            GROUP_NAME: L10n.elideIfLong(this.props.groupsToSet.first().name),
            GROUP_COUNT: this.props.groupsToSet.size(),
            GROUP_COUNT_LOC: L10n.Interpolations.Count
          })}</div>
        {!this.props.isActiveDirectory && FeatureFlags.licenseUponLoginEnabled &&
          <div>
            {this.getLicenseUponLoginCheckbox()}
            {this.getSiteRoleDropdown()}
          </div>
        }
        {this.props.isActiveDirectory &&
          <div>
            {this.getSiteRoleDropdown()}
            {this.state.siteRole === SiteRoles.Unlicensed &&
              <span className="tb-info-message">
                <DialogInfoAlert
                  message={Messages.confirmSetGroupsMinimumSiteRoleWarning()}
                  testId={'group-min-site-role-alert'}
                />
              </span>}
              {FeatureFlags.licenseUponLoginEnabled &&
              <span>
               {this.getLicenseUponLoginCheckbox()}
              </span>
              }
          </div>
        }
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            showSpinner={false}
            clicked={this.props.dismiss}
            testId={'confirm-action-dialog-cancel'}
            title={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className="tb-margin-left-wide">
            <ButtonWithSpinner
              buttonType={ButtonType.Go}
              showSpinner={this.state.busy}
              clicked={this.updateGrantLicenseModeCallback}
              testId={'confirm-action-dialog-submit'}
              title={Messages.confirm()}
              disabled={this.state.busy}
            />
          </span>
        </div>
      </div>
    );
  }

  private handleCheckboxChange = (checked: boolean) => {
    this.setState({grantLicenseModeCheckboxChecked: checked});
  }

  private getLicenseUponLoginCheckbox = (): JSX.Element => {
    return(<div><CheckBoxWithLabelWidget
            checked={this.state.grantLicenseModeCheckboxChecked}
            handleChange={this.handleCheckboxChange}
            containerStyle= {this.CheckBoxContainerStyle}
            label={Messages.grantLicenseUponLoginCheckboxLabel()}
            testId= {this.props.isActiveDirectory ? 'lul-update-ad-group' : 'lul-update-local-group'}
            disabled={(this.props.isActiveDirectory && this.state.siteRole === SiteRoles.Unlicensed)}
          /></div>);
  }

  private getSiteRoleDropdown = () => {
    return(<div className="tb-select-role-dropdown-container tb-row-with-icon-on-right tb-margin-top-wide">
              <SiteRolePickerMenu
                translate={this.translate.instant}
                siteRoleOptions={this.siteRoleOptions}
                getSiteRoleOptionBySiteRole={this.getSiteRoleOptionBySiteRoleCallback}
                onOptionSelected={this.onOptionSelectedCallback}
                selectedRole={SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(this.state.siteRole)}
                disabledRoleOptions={this.props.isActiveDirectory ? [] : [SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(SiteRoles.Unlicensed)]}
                isDisabled={(!this.props.isActiveDirectory && !this.state.grantLicenseModeCheckboxChecked) || this.state.busy}/>
              <div className="tb-row-icon-on-right">
                <SiteRolePopover angularContext={this.props.angularContext}/>
              </div>
            </div>);

  }

  private updateGrantLicenseModeCallback = (): IPromise<any> => {
    this.setState({busy: true});
    const groups = _.map(this.props.groupsToSet.toArray(), (group: IGroupInfo) => group.id);
    const grantLicenseMode: GrantLicenseModes = this.getGrantLicenseMode();
    const siteRole = !this.props.isActiveDirectory && !this.state.grantLicenseModeCheckboxChecked ? SiteRoles.Unlicensed: this.state.siteRole;

    return this.updateGroupsMinimumSiteRole(groups, siteRole, grantLicenseMode);
  }

  private getGrantLicenseMode() {
    let grantLicenseMode: GrantLicenseModes;
    if(!FeatureFlags.licenseUponLoginEnabled){
      grantLicenseMode = undefined;
    } else {
      if(this.state.grantLicenseModeCheckboxChecked){
        grantLicenseMode = GrantLicenseModes.ON_LOGIN;
      }else{
        grantLicenseMode = this.props.isActiveDirectory  ? GrantLicenseModes.ON_SYNC : undefined;
      }
    }
    return grantLicenseMode;
  }

  private handleSuccessResult(groups: Set<IGroupBasicInfo>, siteRole: string) {
    this.toaster.pop({
      type: ToastType.Info,
      text: Messages.updateGroupsSiteRoleSettingsSuccess({
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        COUNT_LOC: L10n.Interpolations.Count
      })
    });
  }

  private handleErrorResult(groups: Set<IGroupBasicInfo>, siteRole: string, result: IResult) {
    this.toaster.pop({
      type: ToastType.Error,
      text: Messages.updateGroupsMinimumSiteRoleError({
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        SITE_ROLE: this.$filter<ISiteRoleFilter>('siteRole')(siteRole),
        COUNT_LOC: L10n.Interpolations.Count
      })
    });
    return this.$q.reject(result); // for chaining errors
  }

  private onSiteRoleSelected(siteRoleOption: SiteRoleOption): void {
    let grantLicenseModeCheckboxChecked = (this.props.isActiveDirectory && siteRoleOption.role === SiteRoles.Unlicensed) ?
                  false : this.state.grantLicenseModeCheckboxChecked;
    this.setState({siteRole: siteRoleOption.role, grantLicenseModeCheckboxChecked});
  }

  public updateGroupsMinimumSiteRole = (groups: string[], siteRole: string, grantLicenseMode: GrantLicenseModes): IPromise<any> => {
    return toQPromise(
      Groups.updateGroupsMinimumSiteRole(groups, siteRole, grantLicenseMode)
        .then(ResultHelper.rejectResultOnError(this.$q))
        .then(() => {
          this.handleSuccessResult(this.props.groupsToSet, siteRole);
          this.props.close({});
        })
        .catch((result: IResult) => this.handleErrorResult(this.props.groupsToSet, siteRole, result))
    );
  }
}
